import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Statistic,
  Select,
  Modal,
  Input,
  message,
} from "antd";
import {
  PlusOutlined,
  SyncOutlined,
  DownloadOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { useBudget } from "../../../hooks/finance/useBudget";
import BudgetTable from "../../../components/finance/budget/BudgetTable";
import BudgetDetailDrawer from "../../../components/finance/budget/BudgetDetailDrawer";
import CreateBudgetModal from "../../../components/finance/budget/CreateBudgetModal";
import DepartmentManagementDrawer from "../../../components/finance/budget/DepartmentManagementDrawer";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { Option } = Select;

const BudgetManagement = () => {
  const navigate = useNavigate();
  const {
    departments,
    subjects,
    budgets,
    loading,
    fetchDepartments,
    fetchSubjects,
    fetchBudgets,
    createBudget,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    submitBudget,
    approveBudget,
    activateBudget,
    deleteBudget,
    getStatistics,
  } = useBudget();

  const [staffList, setStaffList] = useState([]);
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [departmentDrawerVisible, setDepartmentDrawerVisible] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [creating, setCreating] = useState(false);

  const [filters, setFilters] = useState({
    departmentId: null,
    fiscalYear: null,
    term: null,
    status: null,
  });
  const [budgetCodeSearch, setBudgetCodeSearch] = useState("");

  const fetchStaffList = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.auth.listStaff,
        null,
        navigate,
      );
      if (response.ok) {
        setStaffList(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching staff list:", error);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDepartments();
    fetchSubjects();
    fetchBudgets();
    fetchStaffList();
  }, []);

  useEffect(() => {
    fetchBudgets(filters);
  }, [filters]);

  const handleViewDetails = (budget) => {
    setSelectedBudget(budget);
    setDetailsDrawerVisible(true);
  };

  const handleSubmit = async (budget) => {
    Modal.confirm({
      title: "Submit Budget",
      content: `Submit budget for ${budget.departmentName} (${budget.fiscalYear}) for approval?`,
      okText: "Yes, Submit",
      okType: "primary",
      onOk: async () => {
        await submitBudget(budget.budgetId);
      },
    });
  };

  const handleApprove = async (budget) => {
    Modal.confirm({
      title: "Approve & Activate Budget",
      content: `Approve and activate the budget for ${budget.departmentName} (${budget.fiscalYear})?`,
      okText: "Approve & Activate",
      okType: "primary",
      onOk: async () => {
        if (budget.status === "Approved") {
          await activateBudget(budget.budgetId);
        } else {
          await approveBudget(budget.budgetId);
        }
      },
    });
  };

  const handleReject = async (budget, remarks) => {
    // remarks may be provided directly from the drawer's reject modal
    if (remarks !== undefined) {
      await approveBudget(budget.budgetId, "reject", remarks);
      return;
    }
    // Fallback: called from table action (no drawer) — show inline confirm with textarea
    let rejectRemarks = "";
    Modal.confirm({
      title: "Reject Budget",
      content: (
        <div>
          <p>{`Reject budget for ${budget.departmentName} (${budget.fiscalYear}) and return it to draft?`}</p>
          <Input.TextArea
            rows={3}
            placeholder="Enter reason for rejection..."
            onChange={(e) => {
              rejectRemarks = e.target.value;
            }}
          />
        </div>
      ),
      okText: "Yes, Reject",
      okType: "danger",
      onOk: async () => {
        await approveBudget(budget.budgetId, "reject", rejectRemarks);
      },
    });
  };

  const handleDelete = async (budget) => {
    Modal.confirm({
      title: "Delete Budget",
      content: `Delete budget for ${budget.departmentName} (${budget.fiscalYear})? This cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      onOk: async () => {
        await deleteBudget(budget.budgetId);
      },
    });
  };

  const handleCreateBudget = async (data) => {
    setCreating(true);
    try {
      const result = await createBudget(data);
      if (result) {
        setCreateModalVisible(false);
        return true;
      }
      return false;
    } finally {
      setCreating(false);
    }
  };

  const stats = getStatistics();

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const exportToExcel = () => {
    const exportData = budgets.map((b) => ({
      Item: b.departmentName || "-",
      "Budget Code": b.budgetCode || "",
      "Fiscal Year": b.fiscalYear,
      "Academic Year": b.acadyr,
      "Total Amount": parseFloat(b.totalAmount || 0),
      "Amount Spent": parseFloat(b.amountSpent || 0),
      "Amount Committed": parseFloat(b.amountCommitted || 0),
      "Available Balance": parseFloat(b.availableBalance || 0),
      Status: b.status,
      "Created At": b.createdAt
        ? dayjs(b.createdAt).format("DD MMM YYYY")
        : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Budgets");
    XLSX.writeFile(wb, `budgets_${dayjs().format("YYYY-MM-DD")}.xlsx`);
    message.success("Export completed");
  };

  const visibleBudgets = budgets.filter((budget) =>
    (budget.budgetCode || "")
      .toLowerCase()
      .includes(budgetCodeSearch.trim().toLowerCase()),
  );

  // Get fiscal years from budgets
  const fiscalYears = [...new Set(budgets.map((b) => b.fiscalYear))]
    .sort()
    .reverse();

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Statistics Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Budgets"
                value={stats.total}
                valueStyle={{ color: "#1890ff" }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active Budgets"
                value={stats.active}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Pending Approval"
                value={stats.submitted}
                valueStyle={{ color: "#faad14" }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Draft Budgets"
                value={stats.draft}
                valueStyle={{ color: "#888" }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Financial Summary */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Allocated"
                value={formatCurrency(stats.totalAllocated)}
                prefix="UGX"
                valueStyle={{ color: "#1890ff", fontSize: "20px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Spent"
                value={formatCurrency(stats.totalSpent)}
                prefix="UGX"
                valueStyle={{ color: "#ff4d4f", fontSize: "20px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Committed"
                value={formatCurrency(stats.totalCommitted)}
                prefix="UGX"
                valueStyle={{ color: "#faad14", fontSize: "20px" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Content */}
        <Card
          title={
            <Space>
              <UnorderedListOutlined />
              <span>Item Budgets</span>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {/* Action Buttons */}
            <Row gutter={[8, 8]}>
              <Col xs={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <Button
                    icon={<BankOutlined />}
                    onClick={() => setDepartmentDrawerVisible(true)}
                  >
                    Manage Items ({departments.length})
                  </Button>
                  <Space wrap>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setCreateModalVisible(true)}
                    >
                      New Budget
                    </Button>
                    <Button
                      icon={<SyncOutlined />}
                      onClick={() => fetchBudgets(filters)}
                      loading={loading}
                    >
                      Refresh
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={exportToExcel}
                      disabled={budgets.length === 0}
                    >
                      Export
                    </Button>
                  </Space>
                </div>
              </Col>
            </Row>

            {/* Filters */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Input.Search
                  placeholder="Search by budget code"
                  allowClear
                  value={budgetCodeSearch}
                  onChange={(event) => setBudgetCodeSearch(event.target.value)}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="Filter by Item"
                  style={{ width: "100%" }}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={filters.departmentId}
                  onChange={(value) =>
                    setFilters({ ...filters, departmentId: value })
                  }
                >
                  {departments.map((d) => (
                    <Option
                      key={d.departmentId}
                      value={d.departmentId}
                      label={`${d.name} ${d.code || ""} ${d.description || ""}`}
                    >
                      {d.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="Filter by Academic Year"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.fiscalYear}
                  onChange={(value) =>
                    setFilters({ ...filters, fiscalYear: value })
                  }
                >
                  {fiscalYears.map((y) => (
                    <Option key={y} value={y}>
                      {y}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="Filter by Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.status}
                  onChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                >
                  <Option value="Draft">Draft</Option>
                  <Option value="Submitted">Submitted</Option>
                  <Option value="Active">Active</Option>
                  <Option value="Closed">Closed</Option>
                </Select>
              </Col>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="Filter by Term"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.term}
                  onChange={(value) => setFilters({ ...filters, term: value })}
                >
                  <Option value="T1">Term 1</Option>
                  <Option value="T2">Term 2</Option>
                  <Option value="T3">Term 3</Option>
                </Select>
              </Col>
            </Row>

            {/* Budget Table */}
            <BudgetTable
              budgets={visibleBudgets}
              loading={loading}
              onViewDetails={handleViewDetails}
              onSubmit={handleSubmit}
              onApprove={handleApprove}
              onReject={handleReject}
              onDelete={handleDelete}
            />
          </Space>
        </Card>
      </Space>

      {/* Detail Drawer */}
      <BudgetDetailDrawer
        visible={detailsDrawerVisible}
        onClose={() => {
          setDetailsDrawerVisible(false);
          setSelectedBudget(null);
        }}
        budgetId={selectedBudget?.budgetId}
        onSubmit={handleSubmit}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      {/* Create Budget Modal */}
      <CreateBudgetModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onSubmit={handleCreateBudget}
        departments={departments}
        loading={creating}
      />

      {/* Department Management Drawer */}
      <DepartmentManagementDrawer
        visible={departmentDrawerVisible}
        onClose={() => setDepartmentDrawerVisible(false)}
        departments={departments}
        budgets={budgets}
        loading={loading}
        onCreateDepartment={createDepartment}
        onUpdateDepartment={updateDepartment}
        onDeleteDepartment={deleteDepartment}
        staffList={staffList}
        subjects={subjects}
      />
    </>
  );
};

export default BudgetManagement;
